import { isNumber } from "./isNumber";
import BigNumber from "bignumber.js";

/**
 * Rounding modes matching BigNumber.js rounding modes
 */
export enum Rounds {
  Up = "up", // Rounds away from zero (1.1 → 2, -1.1 → -2)
  Down = "down", // Rounds toward zero (1.9 → 1, -1.9 → -1)
  Ceil = "ceil", // Rounds toward +Infinity (1.1 → 2, -1.1 → -1)
  Floor = "floor", // Rounds toward -Infinity (1.9 → 1, -1.1 → -2)
  HalfUp = "halfUp", // Round to nearest, ties away from zero (1.5 → 2, -1.5 → -2)
  HalfDown = "halfDown", // Round to nearest, ties toward zero (1.5 → 1, -1.5 → -1)
  HalfEven = "halfEven", // Round to nearest, ties to even (1.5 → 2, 2.5 → 2, -1.5 → -2)
  HalfCeil = "halfCeil", // Round to nearest, ties toward +Infinity (1.5 → 2, -1.5 → -1)
  HalfFloor = "halfFloor", // Round to nearest, ties toward -Infinity (1.5 → 1, -1.5 → -2)
}

/**
 * Rounds a number to specified decimal places using BigNumber.js
 * @param value - The number to round
 * @param decimalPlaces - Number of decimal places (default: 0)
 * @param round - Rounding mode (default: HalfUp)
 * @param returnNaN - If true, returns NaN for invalid input instead of throwing error
 * @returns Rounded number
 * @throws TypeError if value is not a number and returnNaN is false
 */
export const round = (value: number, decimalPlaces = 0, round = Rounds.HalfUp, returnNaN?: boolean): number => {
  if (!isNumber(value)) {
    if (returnNaN) return NaN;
    throw new TypeError("value parameter is not a number");
  }

  const num = new BigNumber(value);

  const roundingModes = {
    [Rounds.Up]: BigNumber.ROUND_UP,
    [Rounds.Down]: BigNumber.ROUND_DOWN,
    [Rounds.Ceil]: BigNumber.ROUND_CEIL,
    [Rounds.Floor]: BigNumber.ROUND_FLOOR,
    [Rounds.HalfUp]: BigNumber.ROUND_HALF_UP,
    [Rounds.HalfDown]: BigNumber.ROUND_HALF_DOWN,
    [Rounds.HalfEven]: BigNumber.ROUND_HALF_EVEN,
    [Rounds.HalfCeil]: BigNumber.ROUND_HALF_CEIL,
    [Rounds.HalfFloor]: BigNumber.ROUND_HALF_FLOOR,
  };

  const roundingMode = roundingModes[round] || BigNumber.ROUND_HALF_UP;

  return num.decimalPlaces(decimalPlaces, roundingMode).toNumber();
};
